import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';

import i18n from '@/lib/i18n';

const DevicePermissionsContext = createContext(null);

/**
 * Provider pour gérer les permissions du device (géolocalisation, caméra, etc.)
 * Centralise la gestion de toutes les permissions navigateur
 */
export function DevicePermissionsProvider({ children }) {
    // ========================
    // GÉOLOCALISATION
    // ========================
    const [geoPermissionStatus, setGeoPermissionStatus] = useState('prompt');
    const [isGeoEnabled, setIsGeoEnabled] = useState(() => {
        const saved = localStorage.getItem('horodaty_geolocation_enabled');
        return saved === 'true';
    });
    const [geoPosition, setGeoPosition] = useState(null);
    const [isGeoLoading, setIsGeoLoading] = useState(false);

    const isGeolocationSupported = typeof navigator !== 'undefined' && 'geolocation' in navigator;

    const checkGeoPermission = useCallback(async () => {
        if (!isGeolocationSupported) {
            setGeoPermissionStatus('unavailable');
            return 'unavailable';
        }

        try {
            if ('permissions' in navigator) {
                const result = await navigator.permissions.query({ name: 'geolocation' });
                setGeoPermissionStatus(result.state);

                result.addEventListener('change', () => {
                    setGeoPermissionStatus(result.state);
                    if (result.state === 'denied') {
                        setIsGeoEnabled(false);
                        localStorage.setItem('horodaty_geolocation_enabled', 'false');
                    }
                });

                return result.state;
            }
            return 'prompt';
        } catch (error) {
            console.warn('Impossible de vérifier la permission de géolocalisation:', error);
            return 'prompt';
        }
    }, [isGeolocationSupported]);

    const requestGeoPermission = useCallback(async () => {
        if (!isGeolocationSupported) {
            toast.error(i18n.t('common:geolocation.unavailableOnDevice'));
            return false;
        }

        setIsGeoLoading(true);

        try {
            let pos;
            try {
                // Essai avec haute précision (GPS)
                pos = await new Promise((resolve, reject) => {
                    navigator.geolocation.getCurrentPosition(resolve, reject, {
                        enableHighAccuracy: true,
                        timeout: 30000,
                        maximumAge: 0,
                    });
                });
            } catch (highAccError) {
                if (highAccError.code === 3 || highAccError.code === 2) {
                    // Timeout ou position indisponible : fallback sans haute précision (réseau/Wi-Fi)
                    pos = await new Promise((resolve, reject) => {
                        navigator.geolocation.getCurrentPosition(resolve, reject, {
                            enableHighAccuracy: false,
                            timeout: 15000,
                            maximumAge: 60000,
                        });
                    });
                } else {
                    throw highAccError;
                }
            }

            setGeoPosition({
                latitude: pos.coords.latitude,
                longitude: pos.coords.longitude,
                accuracy: pos.coords.accuracy,
                timestamp: pos.timestamp,
            });
            setGeoPermissionStatus('granted');
            setIsGeoEnabled(true);
            localStorage.setItem('horodaty_geolocation_enabled', 'true');
            toast.success(i18n.t('common:geolocation.enabled'));
            return true;
        } catch (error) {
            console.error('Erreur géolocalisation:', error);

            if (error.code === 1) {
                setGeoPermissionStatus('denied');
                toast.error(i18n.t('common:geolocation.permissionDenied'));
            } else if (error.code === 2) {
                toast.error(i18n.t('common:geolocation.positionUnavailable'));
            } else if (error.code === 3) {
                toast.error(i18n.t('common:geolocation.timeout'));
            }

            setIsGeoEnabled(false);
            localStorage.setItem('horodaty_geolocation_enabled', 'false');
            return false;
        } finally {
            setIsGeoLoading(false);
        }
    }, [isGeolocationSupported]);

    const toggleGeolocation = useCallback(async () => {
        if (isGeoEnabled) {
            setIsGeoEnabled(false);
            setGeoPosition(null);
            localStorage.setItem('horodaty_geolocation_enabled', 'false');
            toast.info(i18n.t('common:geolocation.disabled'));
            return false;
        } else {
            const currentPermission = await checkGeoPermission();

            if (currentPermission === 'denied') {
                toast.error(i18n.t('common:geolocation.blockedByBrowser'), { duration: 5000 });
                return false;
            }

            return await requestGeoPermission();
        }
    }, [isGeoEnabled, checkGeoPermission, requestGeoPermission]);

    const getCurrentPosition = useCallback(async () => {
        if (!isGeoEnabled || !isGeolocationSupported) {
            return null;
        }

        try {
            let pos;
            try {
                pos = await new Promise((resolve, reject) => {
                    navigator.geolocation.getCurrentPosition(resolve, reject, {
                        enableHighAccuracy: true,
                        timeout: 30000,
                        maximumAge: 60000,
                    });
                });
            } catch (highAccError) {
                if (highAccError.code === 3 || highAccError.code === 2) {
                    pos = await new Promise((resolve, reject) => {
                        navigator.geolocation.getCurrentPosition(resolve, reject, {
                            enableHighAccuracy: false,
                            timeout: 15000,
                            maximumAge: 120000,
                        });
                    });
                } else {
                    throw highAccError;
                }
            }

            const newPosition = {
                latitude: pos.coords.latitude,
                longitude: pos.coords.longitude,
                accuracy: pos.coords.accuracy,
                timestamp: pos.timestamp,
            };
            setGeoPosition(newPosition);
            return newPosition;
        } catch (error) {
            console.error('Erreur récupération position:', error);
            return null;
        }
    }, [isGeoEnabled, isGeolocationSupported]);

    // ========================
    // CAMÉRA
    // ========================
    const [cameraPermissionStatus, setCameraPermissionStatus] = useState('prompt');
    const [isCameraLoading, setIsCameraLoading] = useState(false);

    const isCameraSupported =
        typeof navigator !== 'undefined' && 'mediaDevices' in navigator && 'getUserMedia' in navigator.mediaDevices;

    const checkCameraPermission = useCallback(async () => {
        if (!isCameraSupported) {
            setCameraPermissionStatus('unavailable');
            return 'unavailable';
        }

        try {
            if ('permissions' in navigator) {
                const result = await navigator.permissions.query({ name: 'camera' });
                setCameraPermissionStatus(result.state);

                result.addEventListener('change', () => {
                    setCameraPermissionStatus(result.state);
                });

                return result.state;
            }
            return 'prompt';
        } catch {
            // Certains navigateurs ne supportent pas la query 'camera'
            return 'prompt';
        }
    }, [isCameraSupported]);

    const requestCameraPermission = useCallback(async () => {
        if (!isCameraSupported) {
            toast.error(i18n.t('common:camera.unavailableOnDevice'));
            return false;
        }

        setIsCameraLoading(true);

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            // Arrêter immédiatement le stream, on voulait juste la permission
            stream.getTracks().forEach((track) => track.stop());

            setCameraPermissionStatus('granted');
            toast.success(i18n.t('common:camera.permissionGranted'));
            return true;
        } catch (error) {
            console.error('Erreur caméra:', error);

            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                setCameraPermissionStatus('denied');
                toast.error(i18n.t('common:camera.permissionDeniedWithHint'));
            } else if (error.name === 'NotFoundError') {
                setCameraPermissionStatus('unavailable');
                toast.error(i18n.t('common:camera.notDetected'));
            } else {
                toast.error(i18n.t('common:camera.accessError'));
            }

            return false;
        } finally {
            setIsCameraLoading(false);
        }
    }, [isCameraSupported]);

    /**
     * Ouvre un stream caméra avec fallback progressif et timeout de sécurité
     * @param {Object} constraints - Contraintes MediaStream (video, audio, etc.)
     * @returns {Promise<MediaStream|null>}
     */
    const getCameraStream = useCallback(
        async (constraints = { video: { facingMode: 'environment' } }) => {
            if (!isCameraSupported) {
                toast.error(i18n.t('common:camera.unavailable'));
                return null;
            }

            // Helper : getUserMedia avec timeout pour éviter les blocages infinis
            const getUserMediaWithTimeout = (mediaConstraints, timeoutMs = 10000) => {
                return Promise.race([
                    navigator.mediaDevices.getUserMedia(mediaConstraints),
                    new Promise((_, reject) =>
                        setTimeout(() => reject(new DOMException('Timeout getUserMedia', 'TimeoutError')), timeoutMs),
                    ),
                ]);
            };

            // Stratégie 1 : essayer avec les contraintes demandées
            try {
                const stream = await getUserMediaWithTimeout(constraints);
                setCameraPermissionStatus('granted');
                return stream;
            } catch (error) {
                console.warn('[Camera] Stratégie 1 échouée:', error.name, error.message);
                if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                    setCameraPermissionStatus('denied');
                    toast.error(i18n.t('common:camera.permissionDenied'));
                    return null;
                }
            }

            // Stratégie 2 : essayer avec { video: true } (sans contraintes)
            try {
                const stream = await getUserMediaWithTimeout({ video: true });
                setCameraPermissionStatus('granted');
                return stream;
            } catch (error) {
                console.warn('[Camera] Stratégie 2 échouée:', error.name, error.message);
                if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                    setCameraPermissionStatus('denied');
                    toast.error(i18n.t('common:camera.permissionDenied'));
                    return null;
                }
            }

            // Stratégie 3 : énumérer les devices et utiliser un deviceId explicite
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                const videoDevices = devices.filter((d) => d.kind === 'videoinput' && d.deviceId);

                if (videoDevices.length === 0) {
                    setCameraPermissionStatus('unavailable');
                    toast.error(i18n.t('common:camera.notDetected'));
                    return null;
                }

                // Préférer la caméra arrière si demandée, sinon prendre la première
                const wantBack = constraints?.video?.facingMode === 'environment';
                const preferred = wantBack
                    ? videoDevices.find(
                          (d) =>
                              d.label.toLowerCase().includes('back') ||
                              d.label.toLowerCase().includes('arrière') ||
                              d.label.toLowerCase().includes('rear') ||
                              d.label.toLowerCase().includes('environment'),
                      )
                    : videoDevices.find(
                          (d) =>
                              d.label.toLowerCase().includes('front') ||
                              d.label.toLowerCase().includes('avant') ||
                              d.label.toLowerCase().includes('user'),
                      );

                const device = preferred || videoDevices[0];

                const stream = await getUserMediaWithTimeout({
                    video: { deviceId: { exact: device.deviceId } },
                });
                setCameraPermissionStatus('granted');
                return stream;
            } catch (error) {
                console.error('[Camera] Stratégie 3 échouée:', error.name, error.message);
            }

            // Toutes les stratégies ont échoué
            toast.error(i18n.t('common:camera.unreachable'));
            return null;
        },
        [isCameraSupported],
    );

    // ========================
    // EFFETS D'INITIALISATION
    // ========================

    // Vérifier les permissions au montage
    useEffect(() => {
        const syncPermissions = async () => {
            const geoStatus = await checkGeoPermission();
            if (geoStatus !== 'granted') {
                setIsGeoEnabled(false);
                localStorage.setItem('horodaty_geolocation_enabled', 'false');
            }
            await checkCameraPermission();
        };
        syncPermissions();
    }, [checkGeoPermission, checkCameraPermission]);

    // Si géoloc activée, récupérer la position initiale
    useEffect(() => {
        if (isGeoEnabled && geoPermissionStatus === 'granted') {
            getCurrentPosition();
        }
    }, [isGeoEnabled, geoPermissionStatus, getCurrentPosition]);

    // ========================
    // VALEUR DU CONTEXTE
    // ========================

    const value = {
        // Géolocalisation
        geolocation: {
            isEnabled: isGeoEnabled,
            isLoading: isGeoLoading,
            permissionStatus: geoPermissionStatus,
            position: geoPosition,
            isSupported: isGeolocationSupported,
            requestPermission: requestGeoPermission,
            toggle: toggleGeolocation,
            getCurrentPosition,
            checkPermission: checkGeoPermission,
        },

        // Caméra
        camera: {
            isLoading: isCameraLoading,
            permissionStatus: cameraPermissionStatus,
            isSupported: isCameraSupported,
            requestPermission: requestCameraPermission,
            checkPermission: checkCameraPermission,
            getStream: getCameraStream,
        },

        // Accès direct aux méthodes fréquentes (raccourcis)
        // Géolocalisation
        isEnabled: isGeoEnabled,
        isLoading: isGeoLoading,
        permissionStatus: geoPermissionStatus,
        position: geoPosition,
        isGeolocationSupported,
        requestPermission: requestGeoPermission,
        toggleGeolocation,
        getCurrentPosition,
        checkPermission: checkGeoPermission,
    };

    return <DevicePermissionsContext.Provider value={value}>{children}</DevicePermissionsContext.Provider>;
}

/**
 * Hook pour accéder à toutes les permissions device
 */
export function useDevicePermissions() {
    const context = useContext(DevicePermissionsContext);
    if (!context) {
        throw new Error('useDevicePermissions doit être utilisé dans un DevicePermissionsProvider');
    }
    return context;
}

/**
 * Hook de rétrocompatibilité pour la géolocalisation
 * @deprecated Utiliser useDevicePermissions().geolocation à la place
 */
export function useGeolocation() {
    const context = useContext(DevicePermissionsContext);
    if (!context) {
        throw new Error('useGeolocation doit être utilisé dans un DevicePermissionsProvider');
    }
    // Retourne la même interface que l'ancien GeolocationProvider
    return {
        isEnabled: context.geolocation.isEnabled,
        isLoading: context.geolocation.isLoading,
        permissionStatus: context.geolocation.permissionStatus,
        position: context.geolocation.position,
        isGeolocationSupported: context.geolocation.isSupported,
        requestPermission: context.geolocation.requestPermission,
        toggleGeolocation: context.geolocation.toggle,
        getCurrentPosition: context.geolocation.getCurrentPosition,
        checkPermission: context.geolocation.checkPermission,
    };
}

/**
 * Hook pour accéder facilement à la caméra
 */
export function useCamera() {
    const context = useContext(DevicePermissionsContext);
    if (!context) {
        throw new Error('useCamera doit être utilisé dans un DevicePermissionsProvider');
    }
    return context.camera;
}
